<template>
  <div>
    <div
      v-if="loaded && withFilters"
      class="top-content-bar">
      <div class="wrapper pt-3 pb-1">
        <validation-observer
          ref="inquiries-filters-form"
          tag="form"
          class="search-form search-form-inquiries">

          <f-button
            v-if="filtersBtnVisible"
            type="button"
            theme="dark"
            class="block full-width mb-3"
            @click.stop="toggleFilters">
            <template v-if="filtersOpen">
              {{ $t('advisersFinder.collapseFilters')}}
            </template>
            <template v-else>
              {{ $t('advisersFinder.expandFilters')}}
            </template>
          </f-button>

          <transition name="slide">
            <div v-if="!filtersBtnVisible || filtersOpen">
              <div class="flex search-form-top">
                <div class="search-form-inputs flex full-width">
                  <f-input-wrapper
                    prependIcon="search"
                    class="search search-field mb-1-ms">
                    <f-input
                      :placeholder="$t('ui.search')"
                      v-model="filters.search"/>
                  </f-input-wrapper>

                  <v-date-picker
                    v-model="lastEventAtRange"
                    is-range
                    mode="dateTime"
                    is24hr
                    :locale="$i18n.locale"
                    class="date">
                    <template v-slot="{ inputValue, togglePopover }">
                      <div class="flex justify-center items-center">
                        <input
                          :value="getDateRangeFormatted(inputValue)"
                          @click="togglePopover()"
                          :placeholder="$t('inquiriesList.lastContact')"
                          class="form-control ml-1 full-width ml-0-ms" />
                      </div>
                    </template>
                  </v-date-picker>
                </div>

                <div class="search-form-buttons flex">
                  <f-button
                    theme="primary"
                    class="ml-1 mb-1-ms ml-0-sm"
                    big
                    @click.prevent="validateFiltersForm">
                    {{ $t('ui.search')}}
                  </f-button>

                  <f-button
                    theme="warning"
                    class="ml-1"
                    big
                    @click.prevent="clearFilters">
                    {{ $t('advisersFinder.resetFilters')}}
                  </f-button>
                </div>
              </div>

              <div class="flex flex-wrap mt-2">
                <f-badge
                  v-for="(inquiryStatus, index) of inquiryStatuses"
                  :key="index"
                  class="mr-1 mb-2"
                  :theme="(inquiryStatus.value).toLowerCase().replaceAll('_', '-')"
                  :checkable="true">
                  <f-checkbox
                    v-model="filters.status[inquiryStatus.value]">
                    {{ inquiryStatus.text }}
                  </f-checkbox>
                </f-badge>
              </div>

              <div class="flex flex-align-items-center hidden-from-desktop-small-only">
                <label for="order" class="mb-0">{{ $t('advisersFinder.sort') }}:</label>
                <v-select
                  class="minimal ml-1 mobile-dropdown-align-left"
                  name="order"
                  :options="orderOptions"
                  v-model="order"
                  :reduce="(item) => { return { column: item.value, direction: item.direction } }"
                  :placeholder="$t('user.selectOrder')"
                  :clearable="false"
                  :searchable="false"
                  label="label">
                </v-select>
              </div>
            </div>
          </transition>
        </validation-observer>
      </div>
    </div>

    <div :class="{'wrapper mt-3': withFilters, 'is-loading': !loaded}">
      <f-list
        ref="all-inquiries-list"
        :class="{
          'inquiries-list': !noMessages,
          'inquiries-list-less-info': noMessages,
          'inquiries-list-customer': userType === 'customer',
        }"
        :loaded="loaded"
        :listSettings="inquiryListSettings"
        :listItems="inquiriesFormatted"
        :itemsSelectable="true"
        :bulkActions="inquiriresBulkActions"
        :itemRoute="itemRoute + ':id'"
        :data-mobile-actions-count="getMobileActionsCount()" />
    </div>

    <template v-if="hasPagination">
      <pagination
        :hasWrapper="true"
        :meta-data="paginationMetaData"
        :elementsCounterLabel="'inquiries'" />
    </template>

    <div
      v-if="!loaded && !loadError"
      class="loading-spinner-wrapper">
      <div class="loading-spinner pb-3 pt-3">
        <span class="loading-spinner-content">
          {{ $t('ui.loadingDataInProgress') }}&hellip;
        </span>
      </div>
    </div>
    <div class="wrapper mt-3">
      <f-alert
        v-if="loadError"
        visible
        theme="warning">
        <span>
          {{ $t('ui.dataLoadError.dataErrorMess') }} <a href="javascript:window.location.reload();" class="lnk lnk-alert">{{ $t('ui.dataLoadError.resetPageButton') }}</a> {{ $t('ui.dataLoadError.forTryAgain') }}
        </span>
      </f-alert>
    </div>
  </div>
</template>

<script>
import Vue from 'vue';
import debounce from 'lodash.debounce';
import FormUtils from '@/utils/FormUtils.js';
import ListUtils from '@/utils/ListUtils.js';
import Pagination from '@/components/core/Pagination.vue';
import HasFilters from '@/mixins/HasFilters.vue';
import HasPagination from '@/mixins/HasPagination.vue';
import InquiryStatuses from '@/data/inquiry-statuses.js';

export default {
  name: 'inquiries-list',
  mixins: [
    HasFilters,
    HasPagination
  ],
  components: {
    'pagination': Pagination
  },
  props: {
    withFilters: {
      type: Boolean
    },
    hasPagination: {
      type: Boolean,
      default: true
    },
    listSource: {
      type: String
    },
    listField: {
      type: String
    },
    requestWhere: {
      type: Object
    },
    noMessages: {
      type: Boolean,
      default: false
    },
    incustomerPreview: {
      type: Boolean,
      default: false
    },
    itemRoute: {
      type: String,
      default: '/oferty-kredytowe/'
    },
    itemsList: {
      type: Array
    },
    whereFilter: {
      type: Object
    }
  },
  computed: {
    inquiryStatuses () {
      return InquiryStatuses;
    },
    inquiriesFormatted () {
      let inquiriesList = this.inquiries;
      if (this.listField) {
        inquiriesList = this.inquiries[this.listField];
      }

      if (!inquiriesList.length) {
        return [];
      }

      return inquiriesList.map(item => {
        if (this.isEdit) {
          item.actionsList = ['open', 'delete'];
        }

        if (!this.noMessages) {
          item.messagesCounter = {
            counter: item.messages_count,
            type: 'mail',
            read: item.new_messages_count === 0
          }
        }

        if (this.userType === 'customer') {
          item.status = {
            status: (item.status).toLowerCase().replaceAll('_', '-'),
            text: this.inquiryStatuses.find(element => element.value === item.status).text
          }


          item.offersCounter = {
            counter: item.bank_offers_count,
            type: 'inquiry',
            read: true
          }
        }

        return item;
      });
    },
    inquiryListSettings () {
      let settingList =
      [
        {
          title: 'ID',
          variableName: ['id'],
          type: 'String',
          sortable: true,
          sortingEvent: 'inquiries-sort-toggle'
        },
        {
          title: this.$t('user.status'),
          variableName: ['status'],
          type: this.userType === 'adviser' ? 'inquiry-status-select' : 'f-badge'
        },
      ];

      if (this.userType === 'customer') {
        settingList.push({
          title: this.$t('user.fullName'),
          variableName: ['adviser_name', 'adviser_surname'],
          type: 'String'
        });
      } else {
        settingList.push({
          title: this.$t('user.fullName'),
          variableName: ['customer_name', 'customer_surname'],
          type: 'String'
        });
      }

      settingList.push({
        title: this.$t('user.city'),
        variableName: ['customer_city'],
        type: 'String'
      });

      settingList.push({
        title: this.$t('inquiriesList.fillingOutTheForm'),
        variableName: ['finished_percent'],
        type: 'f-progress-bar',
        emptyValue: '0'
      });

      if (this.incustomerPreview) {
        settingList.push({
          title: this.$t('inquiriesList.created'),
          variableName: ['created_at'],
          type: 'f-time-elapsed'
        });
      } else {
        settingList.push({
          title: this.$t('customersList.lastInteraction'),
          variableName: ['last_event_at'],
          type: 'f-time-elapsed'
        });
      }

      if (!this.noMessages) {
        if (this.userType === 'customer') {
          settingList.push({
            title: '',
            sortable: false,
            variableName: ['offersCounter'],
            type: 'f-message-counter',
            route: '/oferty-kredytowe/:id/oferty'
          });
        }

        settingList.push({
          title: '',
          sortable: false,
          variableName: ['messagesCounter'],
          type: 'f-message-counter',
          route: '/oferty-kredytowe/:id/lista-wiadomosci'
        });
      }

      if (this.isEdit) {
        settingList.push({
          title: '',
          variableName: ['actionsList'],
          type: 'actions',
          items: [
            {
              type: 'button',
              disabled: false,
              event: 'inquiry-edit',
              label: this.$t('ui.open'),
              name: 'open'
            },
            {
              type: 'button',
              disabled: false,
              label: this.$t('ui.remove'),
              theme: 'danger',
              name: 'delete',
              event: 'inquiry-delete',
            }
          ]
        });
      }

      return settingList;
    },
    inquiriresBulkActions () {
      let bulkActions = [];

      if (this.isEdit) {
        bulkActions.push({
          label: this.$t('inquiriesList.deleteSelectOffers'),
          callbackFunction: this.deleteSelected,
          theme: 'danger'
        })
      }

      return bulkActions;
    }
  },
  watch: {
    'lastEventAtRange.start': function (newValue) {
      if (newValue === null) {
        this.filters.last_event_at_from = null;
      }

      this.filters.last_event_at_from = this.formatDate(newValue);
    },
    'lastEventAtRange.end': function (newValue) {
      if (newValue === null) {
        this.filters.last_event_at_to = null;
      }

      this.filters.last_event_at_to = this.formatDate(newValue);
    }
  },
  data() {
    return {
      isEdit: true,
      inquiries: [],
      loaded: false,
      loadError: false,
      removeInProgress: false,
      selectedItems: [],
      filters: {
        search: '',
        status: {},
        last_event_at_from: '',
        last_event_at_to: '',
        needs_answer: null
      },
      lastEventAtRange: {
        sart: null,
        end: null
      },
      order: {
        column: 'id',
        direction: 'DESC'
      },
      userType: null,
      filtersOpen: false,
      filtersBtnVisible: false,
      orderOptions: [
        {
          label: this.$t('list.fromLatest'),
          value: 'id',
          direction: 'DESC'
        },
        {
          label: this.$t('list.fromOldest'),
          value: 'id',
          direction: 'ASC'
        },
        {
          label: this.$t('inquiriesList.afterModificationDate'),
          value: 'last_event_at',
          direction: 'DESC'
        }
      ]
    };
  },
  mounted () {
    this.$bus.$on('inquiry-delete', this.removeInquiry);
    this.$bus.$on('inquiry-edit', this.editInquiry);
    this.$bus.$on('inquiries-sort-toggle', this.toggleSort);
    this.userType = this.$store.getters['getUserType'].toLowerCase();

    if (!this.listSource) {
      Vue.set(this, 'inquiries', this.itemsList || []);
      this.loaded = true;
    } else {
      this.loadFilteredData();
      this.initFilterStatus();
    }
  },
  methods: {
    debouncedLoadFilteredData: debounce(function () {
      this.$bus.$emit('view-filters-save');
      this.loadFilteredData();
    }, 250),
    loadFilteredData () {
      let endpointParams = {};

      if (this.withFilters) {
        let where = {};

        if (this.whereFilter) {
          for (let i = 0; i < Object.keys(this.whereFilter).length; i++) {
            let key = Object.keys(this.whereFilter)[i];
            this.filters[key] = this.whereFilter[key];
          }
        }

        if (this.filters.search) {
          where.search = this.filters.search;
        }

        if (Object.keys(this.filters.status).filter(item => this.filters.status[item] === true)) {
          where.status = Object.keys(this.filters.status).filter(item => this.filters.status[item] === true);
        }

        if (this.filters.last_event_at_from && this.filters.last_event_at_to) {
          where.last_event_at_from = this.filters.last_event_at_from;
          where.last_event_at_to = this.filters.last_event_at_to;
        }

        if (this.filters.needs_answer) {
          where.needs_answer = this.filters.needs_answer;
        }

        endpointParams.where = where;
        endpointParams.order = this.order
      }

      if (this.requestWhere) {
        endpointParams.where = this.requestWhere;
      }

      let enpointUrl = '/api/' + this.listSource;

      if (this.hasPagination) {
        enpointUrl += '?page=' + this.currentPage
      }

      ListUtils.loadItemsData(this, {
        method: 'get',
        endpoint: enpointUrl,
        listField: 'inquiries',
        pagination: this.hasPagination,
        params: endpointParams,
      });
    },
    clearFilters () {
      this.loaded = false;
      this.loadError = false;
      this.filters = {
        search: '',
        status: {},
        last_event_at_from: '',
        last_event_at_to: '',
        needs_answer: null
      };

      this.lastEventAtRange = {};

      this.order = {
        column: 'last_event_at',
        direction: 'DESC'
      }

      this.loadFilteredData();
      this.$bus.$emit('view-filters-reset');
    },
    validateFiltersForm () {
      this.$bus.$emit('pagination-reset');
      FormUtils.validate(this.$refs['inquiries-filters-form'], this.debouncedLoadFilteredData);
    },
    removeInquiry (inquiry) {
      this.removeInProgress = true;
      let inquiryId = typeof inquiry === 'object' ? inquiry.id : inquiry;

      ListUtils.removeItem(this, {
        endpointBase: '/api/' + this.userType + '/inquiries/delete/',
        id: inquiryId,
        successTitle: this.$t('inquiriesList.deleteOffer'),
        successText: this.$t('inquiriesList.selectOfferDelete'),
        confirmTitle: this.$t('inquiriesList.areYouSureDeleteOffer'),
        confirmText: this.$t('ui.thisOperationCannotUndone'),
        errorTitle: this.$t('ui.errorOccurred'),
        errorText: this.$t('inquiriesList.deleteOfferError'),
        successAction: () => {
          this.removeInProgress = false;
          this.loadFilteredData();
        },
        cancelAction: () => {
          this.removeInProgress = false;
        }
      });
    },
    editInquiry (inquiry) {
      this.$router.push(this.itemRoute + inquiry.id);
    },
    formatDate (timestamp) {
      if (!timestamp) {
        return this.$t('ui.none');
      }

      let dateObject = new Date(timestamp);

      return [
        dateObject.getFullYear(),
        dateObject.getMonth() + 1,
        dateObject.getDate()
      ].map(n => n < 10 ? '0' + n : n).join('-') + ' ' + [
        dateObject.getHours(),
        dateObject.getMinutes()
      ].map(n => n < 10 ? '0' + n : n).join(':');
    },
    getDateRangeFormatted (dateRange) {
      if (!dateRange || (!dateRange.start && !dateRange.end)) {
        return null;
      }

      let dateRangeStart = dateRange.start;
      let dateRangeEnd = dateRange.end;

      if (!dateRangeStart) {
        dateRangeStart = this.$t('ui.from');
      }
      if (!dateRangeEnd) {
        dateRangeEnd = this.$t('ui.to');
      }

      return dateRangeStart + ' - ' + dateRangeEnd;
    },
    toggleSort (param) {
      this.order.column = param.columnName[0];
      this.order.direction = this.order.direction === 'ASC' ? 'DESC' : 'ASC';
      this.loadFilteredData();
    },
    deleteSelected (items) {
      this.removeInProgress = true;
      let itemsToRemove = Object.keys(items).filter(key => items[key] === true);

      ListUtils.bulkActionItems(this, {
        endpoint: '/api/adviser/inquiries/delete-many',
        successTitle: this.$t('inquiriesList.deleteOffers'),
        successText: this.$t('inquiriesList.selectOffersDelete'),
        confirmTitle: this.$t('inquiriesList.areYouSureDeleteOffers'),
        confirmText: this.$t('ui.thisOperationCannotUndone'),
        errorTitle: this.$t('ui.errorOccurred'),
        errorText: this.$t('inquiriesList.deleteOffersError'),
        items: itemsToRemove,
        successAction: () => {
          this.$refs['all-inquiries-list'].deselectAllItems();
          this.removeInProgress = false;
          this.$bus.$emit('pagination-reset');
          this.loadFilteredData();
        },
        cancelAction: () => {
          this.removeInProgress = false;
        }
      });
    },
    initFilterStatus() {
      let tmpStatusfilter = {};
      for (let i = 0; i < this.inquiryStatuses.length; i++) {
        tmpStatusfilter[this.inquiryStatuses[i].value] = false;
      }
      Vue.set(this.filters, 'status', tmpStatusfilter);
    },
    toggleFilters () {
      Vue.set(this, 'filtersOpen', !this.filtersOpen);
    },
    getMobileActionsCount () {
      if (this.userType === 'customer') {
        return 3;
      }

      if (this.incustomerPreview) {
        return 1;
      }

      return 2;
    }
  },
  beforeDestroy () {
    this.$bus.$off('inquiry-delete', this.removeInquiry);
    this.$bus.$off('inquiry-edit', this.editInquiry);
    this.$bus.$off('inquiries-sort-toggle', this.toggleSort);
  }
};
</script>
